// FloatingFeatureWindow — the in-viewer floating window chrome, generalized
// from the JAWS in-viewer feature popup (SuspensionKinematics.tsx). A titled,
// absolutely-positioned card that floats over the viewer's `relative` container:
// header with the title + a pop-out (⧉) button + a close (✕) button, and a
// scrollable body. The card is draggable by its header and resizable from its
// bottom-right corner (via useDraggableResizable), exactly like the Render
// Engine panel.
//
// This is the SolidWorks-style "click a feature-tree item → its window opens in
// the viewer" surface. It owns NO persistent state — the caller (via
// useFeatureWindows) decides which windows are open and which are popped out to
// their own OS window (that OS window uses suspensionKinematics/PopoutWindow,
// imported by the caller). Kept intentionally lightweight (no 3D imports) so
// mounting it near the viewer doesn't re-bloat the Lazy3D critical path
// (viewer/CLAUDE.md).

import { type ReactNode } from 'react';
// lucide-react (same source JAWS' in-viewer popup uses) — the material-symbols
// wrapper in ui/icons doesn't export ExternalLink, and matching JAWS keeps the
// pop-out/close glyphs visually identical across the two feature-window paths.
import { ExternalLink, X } from 'lucide-react';
import { useDraggableResizable } from '../useDraggableResizable';

/** Default window height (px) — also the minimum; the card can't shrink below
 *  it. Roughly the natural height of the JAWS param panels. */
const DEFAULT_HEIGHT = 420;

export interface FloatingFeatureWindowProps {
  /** Header title. */
  title: string;
  /** Window width in px. Default 320 (matches JAWS' non-Geometry panels). */
  width?: number;
  /** Default (and minimum) window height in px. Default 420. */
  height?: number;
  /** Close the window (the ✕ button). */
  onClose: () => void;
  /** Pop the window out into its own OS window (the ⧉ button). Omit to hide
   *  the pop-out affordance for windows that shouldn't detach. */
  onPopOut?: () => void;
  children: ReactNode;
}

export function FloatingFeatureWindow({
  title,
  width = 320,
  height = DEFAULT_HEIGHT,
  onClose,
  onPopOut,
  children,
}: FloatingFeatureWindowProps) {
  // Draggable by the header, resizable from the corner grip; default size is
  // the minimum (can't shrink below it), clamped inside the viewer container.
  const { style, onHeaderPointerDown, resizeHandle } = useDraggableResizable({
    defaultSize: { w: width, h: height },
  });

  return (
    <div
      data-floating-window
      // Offset cut to 1/3 of the old left-3/top-3 (12px → 4px). `style` carries
      // width/height and, once dragged, an explicit left/top that overrides the
      // Tailwind anchor.
      className="absolute left-1 top-1 z-40 flex flex-col overflow-hidden rounded-lg border border-telekinesis-neutral-700 bg-telekinesis-bg-secondary-dark/95 shadow-2xl backdrop-blur-sm"
      style={style}
    >
      <div
        onPointerDown={onHeaderPointerDown}
        className="flex shrink-0 cursor-move select-none items-center justify-between border-b border-telekinesis-neutral-700 px-3 py-2"
      >
        <span className="text-xs font-semibold text-telekinesis-neutral-50">
          {title}
        </span>
        <div className="flex items-center gap-0.5">
          {onPopOut && (
            <button
              type="button"
              onClick={onPopOut}
              onPointerDown={(e) => e.stopPropagation()}
              className="rounded p-0.5 text-telekinesis-text-secondary transition-colors hover:bg-telekinesis-bg-dark hover:text-telekinesis-neutral-50"
              aria-label="Open in a new window"
              title="Open in a new window"
            >
              <ExternalLink className="h-3.5 w-3.5" />
            </button>
          )}
          <button
            type="button"
            onClick={onClose}
            onPointerDown={(e) => e.stopPropagation()}
            className="rounded p-0.5 text-telekinesis-text-secondary transition-colors hover:bg-telekinesis-bg-dark hover:text-telekinesis-neutral-50"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto p-3">{children}</div>
      <div {...resizeHandle} />
    </div>
  );
}
